import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    // Recalculate from the CSS anchor each time; old offsets must not accumulate.
    this.style.removeProperty('left');
    this.style.removeProperty('right');
    this.style.top = OFFSET + 'px';
    const viewportWidth = document.documentElement.clientWidth;
    this.style.maxWidth = `calc(100vw - 32px)`;
    const rect = this.getBoundingClientRect();
    const safeLeft = Math.max(16, Math.min(rect.left, viewportWidth - rect.width - 16));
    if (Math.abs(safeLeft - rect.left) > 0.5) {
      const parentLeft = this.offsetParent?.getBoundingClientRect().left ?? 0;
      this.style.left = `${safeLeft - parentLeft}px`;
      this.style.right = 'auto';
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
